import { useQuery } from '@tanstack/react-query'
import type { CSSProperties, ReactNode } from 'react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowUpFromLine, BadgeCheck, BellRing, ChevronDown, ChevronRight, CircleCheck, Coins, RefreshCw, Scale, ServerCrash, Siren, Undo2, Users } from 'lucide-react'

import type { SystemAlert } from '../api/admin'
import { getAlerts, getCurrentUser, getHealth, getOverview } from '../api/admin'
import { PageHeader, PageSection } from '../components/layout'
import { canOpen } from '../components/layout/Shell'
import { Badge, Button, Card, Count, EmptyState, Skeleton, StatCard, StatusDot, cx, formatNumber, toneClass, type StatCardProps, type Tone } from '../components/ui/Ui'
import { useCopyEnabled } from '../lib/settings'
import styles from './Overview.module.css'

/**
 * What an operator needs on arrival: what is waiting for a decision, what
 * the futures book holds, and whether the platform is healthy right now. The
 * copy-trading tile shows only while `copy.enabled` is on (off since 28 Sep).
 */

type CheckState = 'ok' | 'degraded' | 'down'

const STATE_TONE: Record<CheckState, Tone> = { ok: 'accent', degraded: 'warning', down: 'down' }
const STATE_LABEL: Record<CheckState, string> = { ok: 'в норме', degraded: 'с оговорками', down: 'недоступно' }

/** Readable labels for the readiness checks. */
const CHECK_RU: Record<string, string> = {
  database: 'База данных',
  redis: 'Redis',
  chainWatchers: 'Наблюдатели сетей',
  marketFeed: 'Прайс-фид',
  telegram: 'Telegram-бот',
}

function checkName(key: string): string {
  return CHECK_RU[key] ?? key
}

/** «14 мин», «3 ч 05 мин», «2 д 4 ч»; «меньше минуты» below one. */
function span(ms: number): string {
  const minutes = Math.floor(Math.max(0, ms) / 60_000)
  if (minutes < 1) return 'меньше минуты'
  if (minutes < 60) return `${minutes} мин`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} ч ${String(minutes % 60).padStart(2, '0')} мин`
  return `${Math.floor(hours / 24)} д ${hours % 24} ч`
}

/** «16:47» today, «вчера 16:47», «28.09 16:47» before that. */
function moment(iso: string | null | undefined, now: Date): string {
  if (!iso) return '—'
  const date = new Date(iso)
  const time = date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
  if (date.toDateString() === now.toDateString()) return time
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  if (date.toDateString() === yesterday.toDateString()) return `вчера ${time}`
  return `${date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })} ${time}`
}

/* ------------------------------------------------------------------------ */
/* KPI row                                                                  */
/* ------------------------------------------------------------------------ */

/**
 * A KPI card that opens its screen when this desk may open it (a chevron after
 * the label says so), and stays a plain card otherwise. `alert` tints the top
 * edge when the queue behind it is not empty.
 */
function Kpi({ to, role, alert, label, ...card }: { to: string; role: string | undefined; alert?: Tone } & StatCardProps): JSX.Element {
  const linked = role !== undefined && canOpen(role, to)
  const className = cx(styles.kpi, alert ? toneClass(alert) : undefined)
  const head = linked ? (
    <span className={styles.kpiLabel}>
      {label}
      <ChevronRight className={styles.kpiGo} aria-hidden="true" />
    </span>
  ) : (
    label
  )
  return linked ? (
    <Link to={to} className={cx(className, styles.kpiLink)} data-alert={alert ? '' : undefined}>
      <StatCard label={head} {...card} />
    </Link>
  ) : (
    <div className={className} data-alert={alert ? '' : undefined}>
      <StatCard label={head} {...card} />
    </div>
  )
}

/* ------------------------------------------------------------------------ */
/* attention list                                                           */
/* ------------------------------------------------------------------------ */

interface AttentionItem {
  key: string
  to: string
  tone: Tone
  icon: ReactNode
  title: string
  detail: ReactNode
  count: number
}

function AttentionRow({ item, role, onJump }: { item: AttentionItem; role: string | undefined; onJump?: () => void }): JSX.Element {
  const body = (
    <>
      <span className={cx(styles.attIcon, toneClass(item.tone))}>{item.icon}</span>
      <span className={styles.attText}>
        <span className={styles.attTitle}>{item.title}</span>
        <span className={styles.attDetail}>{item.detail}</span>
      </span>
      <span className={cx(styles.attCount, toneClass(item.tone))}>{formatNumber(item.count)}</span>
    </>
  )
  if (onJump) {
    return (
      <li>
        <button type="button" className={cx(styles.attRow, styles.attAction)} onClick={onJump}>
          {body}
          <ChevronDown className={styles.attGo} aria-hidden="true" />
        </button>
      </li>
    )
  }
  return (
    <li>
      {role !== undefined && canOpen(role, item.to) ? (
        <Link to={item.to} className={cx(styles.attRow, styles.attAction)}>
          {body}
          <ChevronRight className={styles.attGo} aria-hidden="true" />
        </Link>
      ) : (
        <div className={styles.attRow}>{body}</div>
      )}
    </li>
  )
}

/* ------------------------------------------------------------------------ */
/* incidents                                                                */
/* ------------------------------------------------------------------------ */

function OpenIncident({ alert, now }: { alert: SystemAlert; now: Date }): JSX.Element {
  const tone: Tone = alert.severity === 'error' ? 'down' : 'warning'
  return (
    <li className={cx(styles.event, styles.eventOpen, toneClass(tone))}>
      <span className={styles.node} aria-hidden="true">
        <StatusDot tone={tone} pulse />
      </span>
      <div className={styles.eventCard}>
        <div className={styles.eventHead}>
          <span className={styles.eventTitle}>
            {checkName(alert.checkKey)}
            <span className={styles.eventState}>{alert.severity === 'error' ? 'сбой' : 'работа с оговорками'}</span>
          </span>
          <Badge tone={tone} size="sm" pulse>
            открыт
          </Badge>
        </div>
        <div className={styles.eventMeta}>
          <span>
            открыт <time dateTime={alert.openedAt}>{moment(alert.openedAt, now)}</time> · длится {span(now.getTime() - new Date(alert.openedAt).getTime())}
          </span>
          {alert.notifiedAt ? (
            <span className={styles.eventNotified}>
              <BellRing aria-hidden="true" />
              операторы уведомлены
            </span>
          ) : null}
        </div>
        <p className={styles.eventDetail}>{alert.message}</p>
      </div>
    </li>
  )
}

function ResolvedIncident({ alert, now }: { alert: SystemAlert; now: Date }): JSX.Element {
  const [open, setOpen] = useState(false)
  const lasted = alert.resolvedAt ? span(new Date(alert.resolvedAt).getTime() - new Date(alert.openedAt).getTime()) : null
  return (
    <li className={cx(styles.event, styles.eventResolved)} data-open={open ? '' : undefined}>
      <span className={styles.node} aria-hidden="true">
        <span className={styles.nodeDone} />
      </span>
      <div className={styles.resolved}>
        <button type="button" className={styles.resolvedRow} aria-expanded={open} onClick={() => setOpen((value) => !value)} title={open ? undefined : alert.message}>
          <span className={styles.resolvedName}>{checkName(alert.checkKey)}</span>
          <span className={styles.resolvedKind}>
            {alert.severity === 'error' ? 'сбой' : 'с оговорками'}
            {lasted ? ` · ${lasted}` : ''}
          </span>
          <span className={styles.resolvedMessage}>{alert.message}</span>
          <span className={styles.resolvedWhen}>
            <time dateTime={alert.openedAt}>{moment(alert.openedAt, now)}</time>
            {alert.resolvedAt ? (
              <>
                {' → '}
                <time dateTime={alert.resolvedAt}>{moment(alert.resolvedAt, now)}</time>
              </>
            ) : null}
          </span>
          <ChevronDown className={styles.resolvedChevron} aria-hidden="true" />
        </button>
        {open ? <p className={cx(styles.eventDetail, styles.resolvedDetail)}>{alert.message}</p> : null}
      </div>
    </li>
  )
}

/* ------------------------------------------------------------------------ */
/* screen                                                                   */
/* ------------------------------------------------------------------------ */

export default function Overview(): JSX.Element {
  const health = useQuery({
    queryKey: ['health'],
    queryFn: getHealth,
    refetchInterval: 15_000,
    retry: false,
  })
  const overview = useQuery({ queryKey: ['overview'], queryFn: getOverview, refetchInterval: 20_000 })
  const me = useQuery({ queryKey: ['me'], queryFn: getCurrentUser, staleTime: 60_000 })
  const role = me.data?.user.role
  const o = overview.data
  const copyEnabled = useCopyEnabled()
  const alerts = useQuery({ queryKey: ['alerts'], queryFn: getAlerts, refetchInterval: 30_000 })
  const items = alerts.data?.items ?? []
  const openAlerts = items.filter((item) => item.status === 'open')
  // Every open incident first, then the latest resolved ones (eight rows in all, at least three resolved).
  const resolvedAlerts = items.filter((item) => item.status !== 'open').slice(0, Math.max(3, 8 - openAlerts.length))
  const now = new Date()

  // The readiness payload says why a check is not ok in `error` (older builds: `detail`).
  const report = health.data as unknown as
    | { status?: 'ok' | 'degraded'; uptimeSeconds?: number; version?: string; checks?: Record<string, { status: CheckState | 'error'; detail?: string; error?: string; latencyMs?: number }> }
    | undefined
  const checks = Object.entries(report?.checks ?? {})
  const overall: CheckState | null = health.isError ? 'down' : report?.status ? (report.status === 'ok' ? 'ok' : 'degraded') : null
  const failing = checks.filter(([, check]) => check.status !== 'ok').length

  const loading = overview.isLoading
  const pendingWithdrawals = o?.pendingWithdrawals ?? 0
  const needingAttention = o?.withdrawalsNeedingAttention ?? 0
  const pendingKyc = o?.pendingKyc ?? 0
  const pendingReversals = o?.pendingDepositReversals ?? 0

  // What waits for an operator, most urgent first; each row opens its queue.
  const attention: AttentionItem[] = []
  if (o) {
    if (o.withdrawalsNeedingAttention > 0) {
      attention.push({
        key: 'stuck',
        to: '/withdrawals',
        tone: 'down',
        icon: <ArrowUpFromLine />,
        title: 'Отправка выводов не прошла',
        detail: 'Горячий кошелёк не отправил заявку или она зависла',
        count: o.withdrawalsNeedingAttention,
      })
    }
    if (o.pendingDepositReversals > 0) {
      attention.push({
        key: 'reversals',
        to: '/deposit-reversals',
        tone: 'down',
        icon: <Undo2 />,
        title: 'Депозиты исчезли из сети',
        detail: 'Зачисленные депозиты пропали из сети — решите, сторнировать ли',
        count: o.pendingDepositReversals,
      })
    }
    if (o.pendingWithdrawals > 0) {
      attention.push({
        key: 'withdrawals',
        to: '/withdrawals',
        tone: 'warning',
        icon: <ArrowUpFromLine />,
        title: 'Выводы ждут решения',
        detail: o.approvedWithdrawals > 0 ? `Ещё ${formatNumber(o.approvedWithdrawals)} одобрено и ждут отправки` : 'Одобрите или отклоните заявки',
        count: o.pendingWithdrawals,
      })
    } else if (o.approvedWithdrawals > 0) {
      attention.push({
        key: 'approved',
        to: '/withdrawals',
        tone: 'info',
        icon: <ArrowUpFromLine />,
        title: 'Одобренные выводы ждут отправки',
        detail: 'Решение принято, осталось отправить',
        count: o.approvedWithdrawals,
      })
    }
    if (o.pendingKyc > 0) {
      attention.push({
        key: 'kyc',
        to: '/kyc',
        tone: 'warning',
        icon: <BadgeCheck />,
        title: 'Заявки на верификацию',
        detail: 'Новые и на проверке',
        count: o.pendingKyc,
      })
    }
  }
  const incidentItem: AttentionItem | null =
    openAlerts.length > 0
      ? {
          key: 'incidents',
          to: '#incidents',
          tone: openAlerts.some((alert) => alert.severity === 'error') ? 'down' : 'warning',
          icon: <Siren />,
          title: 'Открытые инциденты',
          detail: Array.from(new Set(openAlerts.map((alert) => checkName(alert.checkKey)))).join(', '),
          count: openAlerts.length,
        }
      : null
  const jumpToIncidents = (): void => {
    document.getElementById('incidents')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
  const attentionRows = attention.length + (incidentItem ? 1 : 0)

  const kpiCount = copyEnabled ? 6 : 5

  return (
    <>
      <PageHeader title="Обзор" />

      <div className={styles.page}>
        <div className={styles.kpis}>
          <div className={cx(styles.kpiGrid, 'stagger')} style={{ '--kpi-n': kpiCount } as CSSProperties}>
            <Kpi
              to="/withdrawals"
              role={role}
              alert={needingAttention > 0 ? 'down' : pendingWithdrawals > 0 ? 'warning' : undefined}
              label="Заявки на вывод"
              value={o?.pendingWithdrawals}
              loading={loading}
              icon={<ArrowUpFromLine />}
              tone={needingAttention > 0 ? 'down' : pendingWithdrawals > 0 ? 'warning' : 'neutral'}
            />
            <Kpi
              to="/kyc"
              role={role}
              alert={pendingKyc > 0 ? 'warning' : undefined}
              label="Верификация"
              value={o?.pendingKyc}
              loading={loading}
              icon={<BadgeCheck />}
              tone={pendingKyc > 0 ? 'warning' : 'neutral'}
            />
            <Kpi
              to="/deposit-reversals"
              role={role}
              alert={pendingReversals > 0 ? 'down' : undefined}
              label="Сторнирования"
              value={o?.pendingDepositReversals}
              loading={loading}
              icon={<Undo2 />}
              tone={pendingReversals > 0 ? 'down' : 'neutral'}
            />
            {copyEnabled ? (
              <Kpi
                to="/masters"
                role={role}
                label="Копи-трейдинг"
                value={o?.copyAum}
                decimals={0}
                unit="USDT"
                loading={loading}
                icon={<Coins />}
                tone="neutral"
              />
            ) : null}
            <Kpi
              to="/futures"
              role={role}
              label="Страховой фонд"
              value={o?.insuranceFund}
              decimals={2}
              unit="USDT"
              loading={loading}
              icon={<Scale />}
              tone="accent"
            />
            <Kpi to="/users" role={role} label="Пользователи" value={o?.users} loading={loading} icon={<Users />} tone="neutral" />
          </div>
          {overview.isError ? (
            <div className={styles.kpiError} role="alert">
              <span>Сводка не загрузилась: {o ? 'цифры на момент последнего ответа.' : 'сервер не ответил.'}</span>
              <Button size="sm" variant="ghost" icon={<RefreshCw />} loading={overview.isFetching} onClick={() => void overview.refetch()}>
                Повторить
              </Button>
            </div>
          ) : null}
        </div>

        <div className={styles.split}>
          <Card
            className={styles.column}
            title="Требует внимания"
            actions={attentionRows > 0 ? <Count tone="warning">{attentionRows}</Count> : null}
            flush
          >
            {loading ? (
              <ul className={styles.attList} aria-busy="true" aria-label="Загрузка">
                {[0, 1, 2].map((i) => (
                  <li key={i} className={styles.attRow}>
                    <Skeleton width={32} height={32} radius={8} />
                    <span className={styles.attText}>
                      <Skeleton width="46%" height={12} />
                      <Skeleton width="72%" height={10} />
                    </span>
                  </li>
                ))}
              </ul>
            ) : overview.isError && !o ? (
              <EmptyState compact tone="error" icon={<ServerCrash />} title="Сводка не загрузилась" description="Очереди появятся, когда сервер ответит." />
            ) : attentionRows === 0 ? (
              <EmptyState compact tone="success" icon={<CircleCheck />} title="Очереди пусты" description="Ни вывод, ни верификация, ни депозит не ждут решения." />
            ) : (
              <ul className={cx(styles.attList, 'stagger')}>
                {attention.map((item) => (
                  <AttentionRow key={item.key} item={item} role={role} />
                ))}
                {incidentItem ? <AttentionRow item={incidentItem} role={role} onJump={jumpToIncidents} /> : null}
              </ul>
            )}
          </Card>

          <Card
            className={styles.column}
            title="Состояние систем"
            actions={
              overall ? (
                <Badge tone={STATE_TONE[overall]} pulse={overall !== 'ok'}>
                  {overall === 'ok' ? 'всё в норме' : overall === 'down' ? 'недоступно' : `${failing} из ${checks.length} с оговорками`}
                </Badge>
              ) : null
            }
            flush
          >
            {health.isError ? (
              <EmptyState
                compact
                tone="error"
                icon={<ServerCrash />}
                title="Сервер не отвечает на проверку состояния"
                description="Панель повторит запрос сама; можно проверить сейчас."
                action={
                  <Button size="sm" variant="secondary" icon={<RefreshCw />} loading={health.isFetching} onClick={() => void health.refetch()}>
                    Повторить
                  </Button>
                }
              />
            ) : checks.length === 0 ? (
              <ul className={styles.checkList} aria-busy="true" aria-label="Загружаем состояние сервисов">
                {[0, 1, 2, 3, 4].map((i) => (
                  <li key={i} className={styles.check}>
                    <Skeleton width={8} height={8} circle />
                    <span className={styles.checkText}>
                      <Skeleton width="40%" height={12} />
                    </span>
                    <Skeleton width={64} height={12} />
                  </li>
                ))}
              </ul>
            ) : (
              <ul className={cx(styles.checkList, 'stagger')}>
                {checks.map(([name, check]) => {
                  const state: CheckState = check.status === 'error' ? 'down' : check.status
                  const why = check.error ?? check.detail
                  const tone = STATE_TONE[state]
                  return (
                    <li key={name} className={cx(styles.check, toneClass(tone))} data-state={state}>
                      <StatusDot tone={tone} pulse />
                      <span className={styles.checkText}>
                        <span className={styles.checkName}>
                          {checkName(name)}
                        </span>
                        {why ? (
                          <span className={styles.checkWhy} title={why}>
                            {why}
                          </span>
                        ) : null}
                      </span>
                      <span className={styles.checkState}>{STATE_LABEL[state]}</span>
                    </li>
                  )
                })}
              </ul>
            )}
          </Card>
        </div>

        <PageSection
          id="incidents"
          title="Инциденты"
          actions={
            alerts.data ? (
              openAlerts.length > 0 ? (
                <Badge tone={openAlerts.some((alert) => alert.severity === 'error') ? 'down' : 'warning'} pulse>
                  {openAlerts.length} открыто
                </Badge>
              ) : (
                <Badge tone="accent">всё решено</Badge>
              )
            ) : null
          }
        >
          {alerts.isLoading ? (
            <div className={styles.timelineSkeleton} aria-busy="true" aria-label="Загрузка">
              <Skeleton height={96} radius={12} />
              <Skeleton height={40} radius={8} />
              <Skeleton height={40} radius={8} />
            </div>
          ) : alerts.isError ? (
            <Card>
              <EmptyState
                compact
                tone="error"
                icon={<ServerCrash />}
                title="Журнал инцидентов не загрузился"
                action={
                  <Button size="sm" variant="secondary" icon={<RefreshCw />} loading={alerts.isFetching} onClick={() => void alerts.refetch()}>
                    Повторить
                  </Button>
                }
              />
            </Card>
          ) : items.length === 0 ? (
            <Card>
              <EmptyState compact tone="success" icon={<CircleCheck />} title="Тихо" description="За последнее время ни одна проверка не выходила из нормы." />
            </Card>
          ) : (
            <ol className={cx(styles.timeline, 'stagger')}>
              {openAlerts.map((alert) => (
                <OpenIncident key={alert.id} alert={alert} now={now} />
              ))}
            </ol>
          )}
          {!alerts.isLoading && !alerts.isError && resolvedAlerts.length > 0 ? (
            <details className={styles.resolvedDetails}>
              <summary>Решённые инциденты <span>{resolvedAlerts.length}</span><ChevronDown aria-hidden="true" /></summary>
              <ol className={styles.timeline}>
                {resolvedAlerts.map((alert) => <ResolvedIncident key={alert.id} alert={alert} now={now} />)}
              </ol>
            </details>
          ) : null}
        </PageSection>
      </div>
    </>
  )
}
